import type { MaterialVariance } from "@shared/types";
import { cn } from "@/lib/utils";

interface VarianceHeatmapProps {
  data: MaterialVariance[];
  onSelect?: (material: string) => void;
  selected?: string;
}

export function VarianceHeatmap({ data, onSelect, selected }: VarianceHeatmapProps) {
  if (data.length === 0) {
    return (
      <div className="flex h-40 items-center justify-center text-muted-foreground">
        No material variance data
      </div>
    );
  }

  function getVarianceColor(pct: number): string {
    const abs = Math.abs(pct);
    if (abs < 1) return "bg-emerald-500/20 text-emerald-400";
    if (abs < 3) return "bg-amber-500/20 text-amber-400";
    if (abs < 5) return "bg-orange-500/20 text-orange-400";
    return "bg-red-500/20 text-red-400";
  }

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-border text-left">
            <th className="px-3 py-2 text-muted-foreground font-medium">Material</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Readings</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Avg Target (%)</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Avg Actual (%)</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Variance (%)</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Abs %</th>
            <th className="px-3 py-2 text-right text-muted-foreground font-medium">Std Dev</th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr
              key={row.material}
              className={cn(
                "border-b border-border/50 transition-colors cursor-pointer hover:bg-muted/50",
                selected === row.material && "bg-muted"
              )}
              onClick={() => onSelect?.(row.material)}
            >
              <td className="px-3 py-2 font-medium text-foreground">
                {row.material}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {row.readings.toLocaleString()}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {row.avg_target_pct.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {row.avg_actual_pct.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {row.avg_variance_pct > 0 ? "+" : ""}
                {row.avg_variance_pct.toFixed(2)}
              </td>
              <td className="px-3 py-2 text-right">
                <span
                  className={cn(
                    "inline-block rounded px-2 py-0.5 text-xs font-medium",
                    getVarianceColor(row.avg_variance_pct)
                  )}
                >
                  {Math.abs(row.avg_variance_pct).toFixed(1)}%
                </span>
              </td>
              <td className="px-3 py-2 text-right text-muted-foreground">
                {row.stddev.toFixed(2)}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
